// ============================================================================
//  LibraryTable.js — one CRUD grid that drives every child category tab.
// ----------------------------------------------------------------------------
//  Because every child table shares the same 8-column shape, one component
//  handles medications, labs, and every future category. The `category` prop
//  is passed straight through to the URL — the backend routes are generic.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { templateChild, templateChildRow, templateSuggest } from '../../BaseURL';
import notify from './notify';
import { IconPlus, IconSparkle, IconEdit, IconTrash, IconCheck } from './icons';

// Age scope is set once on the parent template — every child row inherits it,
// so it isn't editable per-row here. No filter, no dropdown in the row editor,
// no column in the table. Suggest AI reuses the parent's age scope.

// Per-category UX copy: labels, placeholders, and body textarea size.
// Documentation categories (complaint, history, exam, other_docs) need lots
// of room for prose; meds and labs stay compact.
const CATEGORY_COPY = {
  medications: {
    hint: 'Each row is one drug option auditors can tick during claim composition.',
    labelName: 'Drug (label)',
    labelPlaceholder: 'e.g. Artemether-Lumefantrine',
    bodyName: 'Dose · frequency · duration · route',
    bodyPlaceholder: '80/480 mg twice daily for 3 days, PO',
    bodyHint: 'Keep it concise — the auditor stamps this text into the claim as-is.',
    rows: 3,
  },
  labs: {
    hint: 'Each row is one lab option with its expected result phrasing.',
    labelName: 'Lab name',
    labelPlaceholder: 'e.g. Blood Film for MPs',
    bodyName: 'Expected result phrasing',
    bodyPlaceholder: 'MP+ 2000 parasites/µL; species P. falciparum',
    bodyHint: 'Reads as a real result. The auditor edits values per patient.',
    rows: 3,
  },
  complaints: {
    hint: 'Each row is a self-contained presenting complaint. Save 3–5 variants.',
    labelName: 'Summary (6–8 words)',
    labelPlaceholder: "e.g. Fever + body aches, 3 days",
    bodyName: 'Complaint (1–2 sentences)',
    bodyPlaceholder: 'Patient presents with a 3-day history of fever, chills, generalised body aches and headache. No vomiting, no diarrhoea.',
    bodyHint: 'Neutral clinical language. No names, no medication doses.',
    rows: 6,
  },
  history: {
    hint: "Each row is the patient's OWN background — past medical, family, social. Save several variants.",
    labelName: 'Summary (6–8 words)',
    labelPlaceholder: 'e.g. No PMHx, non-smoker, family Hx HTN',
    bodyName: 'History (2–4 sentences)',
    bodyPlaceholder: 'No prior admissions. No known chronic illnesses. Non-smoker, non-alcoholic. Father has hypertension. No known drug allergies.',
    bodyHint: 'This is background — NOT what happened during this visit.',
    rows: 7,
  },
  examination: {
    hint: 'Each row is a set of physical exam findings that would support the diagnosis. Save 3–5 patterns.',
    labelName: 'Summary (6–8 words)',
    labelPlaceholder: 'e.g. Pale, splenomegaly, mild dehydration',
    bodyName: 'Examination findings (2–4 sentences)',
    bodyPlaceholder: 'Patient looks unwell, mild pallor of conjunctiva. Mildly dehydrated. Chest clear on auscultation. Spleen palpable 2 cm below costal margin.',
    bodyHint: 'System-by-system. No exact vitals numbers (those live in the vitals template).',
    rows: 7,
  },
  other_docs: {
    hint: 'Each row is the clinical impression / assessment — synthesis of the case.',
    labelName: 'Summary (6–8 words)',
    labelPlaceholder: 'e.g. Uncomplicated, no severity features',
    bodyName: 'Clinical impression (2–3 sentences)',
    bodyPlaceholder: 'Working diagnosis of uncomplicated malaria based on presentation and RDT positive. No severity features noted. Differentials of viral illness and typhoid considered.',
    bodyHint: 'Impression only — the treatment plan lives in its own tab later.',
    rows: 6,
  },
  _default: {
    hint: 'These options appear as pickable items when auditors compose a claim.',
    labelName: 'Label',
    labelPlaceholder: '',
    bodyName: 'Body / template',
    bodyPlaceholder: '',
    bodyHint: 'The text the auditor stamps into the claim.',
    rows: 4,
  },
};

export default function LibraryTable({
  conditionId, conditionName, conditionAgeScope,
  category, categoryLabel, hint,
  onAiSuggested,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | row
  const [suggesting, setSuggesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(templateChild(conditionId, category));
      setRows(data.rows || []);
    } catch (err) {
      notify.error('Failed to load', err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [conditionId, category]);

  useEffect(() => { load(); }, [load]);

  const remove = async (row) => {
    const ok = await notify.confirm('Remove this row?', `${row.label} will be removed from the ${categoryLabel} library.`, {
      confirmText: 'Remove', cancelText: 'Cancel', danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(templateChildRow(conditionId, category, row.id));
      notify.toast.success('Removed');
      load();
    } catch (err) {
      notify.error('Delete failed', err?.response?.data?.error || err.message);
    }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      // Age scope isn't asked here — the parent condition's age_scope is used.
      // If the frontend sends nothing, the backend falls back to cond.age_scope.
      const { data } = await axios.post(templateSuggest(conditionId, category), {
        ageScope: conditionAgeScope || 'both',
        count: 12,
      });
      notify.toast.success(`${data.inserted} suggestions ready — review below`);
      // Auto-open the review drawer filtered to this category so the reviewer
      // sees the new candidates immediately without hunting for the button.
      onAiSuggested?.();
    } catch (err) {
      notify.error('AI suggest failed', err?.response?.data?.error || err.message);
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <>
      <div className="tp-tab-toolbar">
        <div className="tp-tab-title">
          <strong>{categoryLabel}</strong>
          {hint && <span className="tp-hint">{hint}</span>}
        </div>
        <div className="tp-tab-actions">
          <button className="tp-btn tp-btn-ghost" onClick={suggest} disabled={suggesting}>
            <IconSparkle size={14} />
            {suggesting ? 'Asking AI…' : 'Suggest with AI'}
          </button>
          <button className="tp-btn tp-btn-primary" onClick={() => setEditing('new')}>
            <IconPlus size={14} /> Add row
          </button>
        </div>
      </div>

      {loading && <div className="tp-empty">Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="tp-empty">
          <div className="tp-empty-icon"><IconCheck size={28} /></div>
          <h3>No {categoryLabel.toLowerCase()} in the library yet</h3>
          <p>Add rows manually, or press "Suggest with AI" — you review each one before it goes live.</p>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <table className="tp-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Body / template</th>
              <th style={{ width: 90 }}>Used</th>
              <th style={{ width: 100 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td><strong>{r.label}</strong></td>
                <td className="tp-td-body">{r.body_template || <em className="tp-muted">—</em>}</td>
                <td>{r.use_count}</td>
                <td className="tp-td-actions">
                  <button className="tp-icon-btn" onClick={() => setEditing(r)} title="Edit">
                    <IconEdit size={14} />
                  </button>
                  <button className="tp-icon-btn danger" onClick={() => remove(r)} title="Delete">
                    <IconTrash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <RowEditor
          conditionId={conditionId}
          conditionName={conditionName}
          conditionAgeScope={conditionAgeScope}
          category={category}
          categoryLabel={categoryLabel}
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </>
  );
}

/* ── Row editor modal ─────────────────────────────────────────────────── */
function RowEditor({
  conditionId, category, categoryLabel, conditionAgeScope,
  initial, onClose, onSaved,
}) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    label:        initial?.label || '',
    bodyTemplate: initial?.body_template || '',
    // Age scope isn't editable per-row — it always inherits from the parent
    // template (set at creation). Kept in the payload for backend contract.
    ageScope:     initial?.age_scope || conditionAgeScope || 'both',
    isDefault:    !!initial?.is_default,
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.label.trim().length < 1) {
      notify.toast.error('Label is required');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(templateChildRow(conditionId, category, initial.id), form);
      } else {
        await axios.post(templateChild(conditionId, category), form);
      }
      notify.toast.success(isEdit ? 'Row updated' : 'Row added');
      onSaved?.();
    } catch (err) {
      notify.error('Save failed', err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  // Per-category copy — labels/placeholders that fit the doc type. Everything
  // uses the same underlying schema; only the guidance text changes.
  const copy = CATEGORY_COPY[category] || CATEGORY_COPY._default;
  // Documentation categories need much more room for long-form prose than
  // meds/labs. Auto-detect from the copy configuration.
  const bodyRows = copy.rows || 4;
  const isLongForm = bodyRows >= 6;

  return (
    <div className="tp-modal-scrim" onClick={onClose}>
      <div className={`tp-modal ${isLongForm ? 'tp-modal-wide' : ''}`}
           onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <div className="tp-modal-head">
            <h3>{isEdit ? `Edit ${categoryLabel} entry` : `New ${categoryLabel} entry`}</h3>
            <p>{copy.hint}</p>
          </div>

          <div className="tp-field">
            <label>{copy.labelName}</label>
            <input className="tp-input" autoFocus value={form.label}
              placeholder={copy.labelPlaceholder}
              onChange={(e) => update('label', e.target.value)} />
            <span className="tp-hint">A short summary shown in the picker.</span>
          </div>

          <div className="tp-field">
            <label>{copy.bodyName}</label>
            <textarea className="tp-input" rows={bodyRows} value={form.bodyTemplate}
              placeholder={copy.bodyPlaceholder}
              onChange={(e) => update('bodyTemplate', e.target.value)} />
            <span className="tp-hint">{copy.bodyHint}</span>
          </div>

          <div className="tp-field">
            <label className="tp-check-row">
              <input type="checkbox" checked={form.isDefault}
                onChange={(e) => update('isDefault', e.target.checked)} />
              Auto-tick this row by default in the composer
            </label>
          </div>

          <div className="tp-modal-actions">
            <button type="button" className="tp-btn tp-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="tp-btn tp-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add row'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
